import { createFileRoute, Link } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { LogoReferences } from "@/components/calv/LogoReferences";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Capabilities } from "@/components/calv/Capabilities";
import { StackGrid } from "@/components/calv/StackGrid";
import { Callout } from "@/components/calv/Callout";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { RelatedServices } from "@/components/calv/RelatedServices";
import { relatedBySource } from "@/lib/related-services-data";

export const Route = createFileRoute("/leistungen/tracking-datenbasis")({
  head: () => {
    const h = pageHead("trackingDataFoundation", "de");
    return { meta: h.meta, links: h.links };
  },
  component: TrackingPage,
});

const steps = [
  { num: "01", title: "Analyse der bestehenden Tracking- und Datenlandschaft", body: "Wir analysieren, wie Daten aktuell erhoben, verarbeitet und interpretiert werden. GA4-Setup, erfasste Events, Unterschiede zwischen Plattformdaten und Webanalyse, Consent-Mechaniken, Plausibilität der Zahlen. Ziel: Transparenz darüber, welchen Zahlen man vertrauen kann." },
  { num: "02", title: "Definition einer realistischen und nutzbaren Datenbasis", body: "Perfekte Daten gibt es nicht – belastbare Entscheidungsgrundlagen schon. Wir definieren steuerungsrelevante KPIs, geschäftlich relevante Conversions, akzeptable Abweichungen und realistische Attribution. Es geht nicht um absolute Genauigkeit, sondern um verlässliche Entscheidungsfähigkeit." },
  { num: "03", title: "Optimierung von Tracking, Attribution und Datenfluss", body: "Auf Basis der Anforderungen optimieren wir das Tracking-Setup: saubere Event-Definitionen, Bereinigung doppelter Signale, sinnvolle Attributionsmodelle, Berücksichtigung von Consent- und iOS-Einschränkungen. Ziel ist nicht mehr Tracking – sondern besseres Tracking." },
  { num: "04", title: "Reporting mit Einordnung statt Zahlenfriedhof", body: "Daten entfalten ihren Wert erst durch Einordnung. Wir sorgen dafür, dass Reportings die richtigen KPIs enthalten, Entwicklungen über Zeit zeigen, Zusammenhänge sichtbar machen und Entscheidungen vorbereiten – statt zu verwirren. Zahlen ohne Kontext sind gefährlich." },
];

const capabilities = [
  { name: "Tracking-Audit", detail: "Bestandsaufnahme, Datenqualität" },
  { name: "GA4-Setup", detail: "Events, Conversions, Parameter" },
  { name: "Server-side Tracking", detail: "Matomo, wetracked, Custom" },
  { name: "Consent-Management", detail: "TCF, datenschutzkonform" },
  { name: "Attributionsmodelle", detail: "realistisch interpretierbar" },
  { name: "Reporting-Architektur", detail: "KPIs mit Einordnung" },
  { name: "Datenflüsse", detail: "BigQuery, Looker Studio" },
  { name: "Plausibilitätsprüfung", detail: "Plattform vs. Webanalyse" },
];

const stack = [
  { name: "Google Analytics (GA4)", small: "Webanalyse" },
  { name: "Google Tag Manager", small: "Tag-Verwaltung" },
  { name: "Consent-Management-Plattformen", small: "TCF-konform" },
  { name: "Looker Studio", small: "Reporting, Dashboards" },
  { name: "Matomo", small: "Server-side, datenschutzkonform" },
  { name: "wetracked", small: "Spezialisiertes Server-side Setup" },
  { name: "BigQuery", small: "Data Warehouse" },
  { name: "Plattform-APIs", small: "Google, Meta & weitere" },
];

function TrackingPage() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          eyebrow="Tracking & Datenbasis"
          headline={<>Tracking ist <em>nicht Pflichtprogramm.</em> Es ist Fundament.</>}
          lead="Server-side Tracking, Enhanced Conversions, BigQuery-Anbindung, sauberes Consent-Management. Ohne diese Disziplin ist alles, was darüber läuft, geraten."
          breadcrumbs={[{ label: "Start", to: "/" }, { label: "Leistungen", to: "/leistungen" }, { label: "Tracking & Datenbasis" }]}
        />

        <Section number="02" kicker="Standpunkt" headline={<>Zahlen sollen <em>Orientierung</em> geben.</>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">Viele Unternehmen treffen täglich Entscheidungen auf Basis von Zahlen, die unvollständig, verzerrt oder schlicht falsch sind. Plattformen berichten ihren eigenen Erfolg. Datenschutz und Consent verzerren Messungen. Attributionsmodelle liefern widersprüchliche Ergebnisse.</p>
              <p className="mb-5">Das Problem ist nicht fehlendes Tracking. Das Problem ist eine Datenbasis, die keine belastbaren Entscheidungen zulässt. Wir sorgen dafür, dass Zahlen wieder das tun, was sie sollen: Orientierung geben.</p>
              <p className="mb-5">Tracking ist für uns kein Selbstzweck und keine technische Spielerei. Es ist die Grundlage, um zu entscheiden: welche Kanäle Geld verdienen, welche Geld kosten, wo skaliert werden kann, wo gebremst werden muss. Ohne diese Klarheit wird Budget verteilt – nicht gesteuert.</p>
              <p>Wie wir E-Commerce-Tracking konkret in Shopify verankern — von Customer-Events über Server-side-Tracking bis Conversion-API — zeigen wir auf der <Link to="/leistungen/onlineshop-entwicklung/shopify-agentur" className="text-cyan-deep underline-offset-4 hover:underline">Shopify-Agentur-Seite</Link>.</p>
            </div>
          </RevealOnScroll>
          <Callout>Perfekte Daten sind eine Illusion. Schlechte Entscheidungen auf Basis falscher Sicherheit nicht.</Callout>
        </Section>

        <Section variant="tinted" number="03" kicker="Vorgehen" headline={<>Vier Phasen zu einer <em>belastbaren</em> Datenbasis.</>}>
          <ProcessFlow steps={steps} />
          <Callout>Plattformdaten zeigen Leistung. Eine saubere Datenbasis zeigt Wahrheit.</Callout>
          <Callout>Wer ohne Server-side-Tracking steuert, entscheidet auf Basis unvollständiger Realität.</Callout>
        </Section>

        <Section number="04" kicker="Capabilities & Stack" headline={<>Womit wir Tracking <em>belastbar</em> machen.</>}>
          <Capabilities items={capabilities} />
          <div className="mt-12">
            <p className="eyebrow mb-5">Stack</p>
            <StackGrid items={stack} />
          </div>
        </Section>

        <LogoReferences locale="de" number="05" />

        <RelatedServices
          locale="de"
          fromId="tracking-datenbasis"
          number="06"
          items={relatedBySource["tracking-datenbasis"]}
        />

        <Section number="07" kicker="Engagement">
          <CTABlock
            headline={<>Wie <em>belastbar</em> sind Ihre Marketingzahlen wirklich?</>}
            body="Wenn Sie sich nicht sicher sind, ob Ihre Entscheidungen auf einer verlässlichen Datenbasis beruhen, sollten wir darüber sprechen."
            primary={{ label: "Kontakt aufnehmen", to: "/kontakt" }}
            secondary={{ label: "Alle Leistungen", to: "/leistungen" }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
}
